import { FC, useMemo } from 'react';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { FinancialValue } from '@/shared/components/financial-values';
import {
    useReportData,
    useReportQuery,
} from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/hook';
import PlatformTable from '@/shared/components/tables/PlatformTable';
import { ReportComponent } from '@/types';
import { TableConfig } from '@/types/api/modules/business-intelligence';

interface ReportTableProps {
    component: ReportComponent;
}

const formatCellValue = (value: unknown, format?: string) => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    if (typeof value !== 'number') {
        return String(value);
    }
    if (format === 'currency') {
        return <FinancialValue value={value} />;
    }
    if (format === 'percent') {
        return value.toLocaleString(undefined, { style: 'percent', maximumFractionDigits: 2 });
    }

    return <FinancialValue kind='number' value={value} />;
};

const ReportTable: FC<ReportTableProps> = ({ component }) => {
    const { token } = useAntDesignTheme();
    const query = useReportQuery(component);
    const { data } = useReportData(component);
    const config = component.configuration as Partial<TableConfig> | undefined;

    const fields = useMemo(() => {
        if (config?.columns?.length) {
            return config.columns;
        }

        return [...query.dimensions, ...query.metrics].map((field) => ({
            field: field.field,
            label: field.label ?? field.field,
        }));
    }, [config?.columns, query.dimensions, query.metrics]);

    const columns = fields.map((field) => ({
        dataIndex: field.field,
        ellipsis: true,
        key: field.field,
        render: (value: unknown) => formatCellValue(value, 'format' in field ? field.format : undefined),
        title: field.label ?? field.field,
    }));

    return (
        <PlatformTable
            columns={columns}
            dataSource={data.rows.map((row, index) => ({ ...row, __rowKey: index }))}
            locale={{ emptyText: 'No rows match this component configuration.' }}
            pagination={
                data.rows.length > (config?.pagination?.pageSize ?? 25)
                    ? { pageSize: config?.pagination?.pageSize ?? 25, size: 'small' }
                    : false
            }
            rowKey='__rowKey'
            scroll={{ x: 'max-content', y: 'calc(100% - 2.5rem)' }}
            size='small'
            style={{ height: '100%', borderColor: token.colorBorder }}
        />
    );
};

export default ReportTable;
